import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { adminApi } from "../../lib/adminApi";
import { money } from "../../lib/api";
import { Btn, emailToast, run, fmtDateTime } from "../../components/admin/AdminUI";
import { StatusBadge } from "../../components/track/StatusBadge";
import { DetailsTab } from "../../components/admin/shipment/DetailsTab";
import { InvoiceTab } from "../../components/admin/shipment/InvoiceTab";
import { CarrierTab } from "../../components/admin/shipment/CarrierTab";
import { MilestonesTab } from "../../components/admin/shipment/MilestonesTab";
import { NotesTab, EmailsTab } from "../../components/admin/shipment/ActivityTabs";

const TABS = [
  ["details", "Details", DetailsTab], ["invoice", "Invoice & Payment", InvoiceTab], ["carrier", "Carrier", CarrierTab],
  ["milestones", "Milestones", MilestonesTab], ["notes", "Internal notes", NotesTab], ["emails", "Email log", EmailsTab],
];

export default function ShipmentDetail() {
  const { tn } = useParams();
  const [sh, setSh] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/shipments/${tn}`).then((r) => setSh(r.data)).catch(() => setMissing(true));
  }, [tn]);

  const apply = (data) => data && setSh(data.shipment || data);
  const underReview = async () => {
    const r = await run(() => adminApi.post(`/admin/shipments/${tn}/milestones`, { status: "Under Review", note: "Our coordinator is reviewing your route, details and price.", notify: true }), "Status set to Under Review");
    if (r) { apply(r.data); emailToast(r.data.email); }
  };

  if (missing) return <p className="text-slate-600" data-testid="admin-shipment-missing">Shipment not found. <Link to="/admin/shipments" className="text-brand-600 underline">Back to list</Link></p>;
  if (!sh) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="admin-shipment-detail">
      <Link to="/admin/shipments" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-600 mb-4" data-testid="back-to-shipments"><ArrowLeft className="w-4 h-4" />Shipments</Link>
      <div className="panel p-5 sm:p-6 mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono font-bold text-2xl sm:text-3xl text-slate-900 tracking-wider" data-testid="detail-tracking-number">{sh.tracking_number}</h1>
            <StatusBadge status={sh.status} testid="detail-status" />
          </div>
          <div className="mt-2 text-sm text-slate-600">{sh.customer.full_name} · {sh.item_summary} · {sh.pickup.city}, {sh.pickup.state} → {sh.delivery.city}, {sh.delivery.state}</div>
          <div className="mt-1 text-xs text-slate-500">Created {fmtDateTime(sh.created_at)} · Estimate {money(sh.quote.estimate)} · Final {sh.final_price != null ? money(sh.final_price) : "not set"}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {sh.status === "Registered" && <Btn onClick={underReview} data-testid="set-under-review-button">Set Under Review</Btn>}
          <a href={`/track/${sh.tracking_number}`} target="_blank" rel="noreferrer" className="btn-ghost h-10 px-4 text-sm" data-testid="open-public-tracking"><ExternalLink className="w-4 h-4" />Public tracking</a>
          {sh.invoice && <a href={`/invoice/${sh.invoice.token}`} target="_blank" rel="noreferrer" className="btn-ghost h-10 px-4 text-sm" data-testid="open-public-invoice"><FileText className="w-4 h-4" />Invoice page</a>}
        </div>
      </div>
      <Tabs defaultValue="details">
        <TabsList className="h-auto flex-wrap justify-start bg-white border border-slate-200 rounded-none p-1 mb-6">
          {TABS.map(([v, label]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 data-[state=active]:shadow-none text-slate-700">{label}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([v, , C]) => <TabsContent key={v} value={v}><C sh={sh} apply={apply} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
